import { z } from 'zod';
import { sectionHasValue } from '../util';
import { registry } from '../registry';
export const ostomyCardTitles = {
    root: 'Ostomy',
    typeAndStatus: 'Type & Status',
    mucosalIntegrity: 'Mucosal Integrity',
    peristomalSkin: 'Peristomal Skin',
    selfManagement: 'Self-Management & Caregiver (OASIS M2102)',
    whoManages: 'Who Manages Ostomy Care',
    education: 'Education (OASIS M2010)',
    educationTopics: 'Topics Addressed This Visit',
    learner: 'Learner',
    dialysis: 'Dialysis',
    narrative: 'Narrative',
};
export const baseOstomySchema = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    present: z
        .string()
        .optional()
        .register(registry, {
        label: 'Ostomy Present',
        options: [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
        ],
    }),
    location: z.string().optional().register(registry, { label: 'Location' }),
    status: z
        .string()
        .optional()
        .register(registry, {
        label: 'Status',
        options: [
            { value: 'patent', label: 'Patent' },
            { value: 'obstructed', label: 'Obstructed' },
            { value: 'bagd', label: 'Bagged' },
        ],
    }),
    amtDrainage: z.string().optional().register(registry, { label: 'Amt of Drainage (ml)' }),
    ostomyType: z
        .string()
        .optional()
        .register(registry, {
        label: 'Ostomy Type',
        options: [
            { value: 'colostomy-sigmoid', label: 'Colostomy - sigmoid' },
            { value: 'colostomy-descending', label: 'Colostomy - descending' },
            { value: 'colostomy-transverse', label: 'Colostomy - transverse' },
            { value: 'colostomy-ascending', label: 'Colostomy - ascending' },
            { value: 'ileostomy', label: 'Ileostomy' },
            { value: 'urostomy', label: 'Urostomy / ileal conduit' },
            { value: 'loop-colostomy', label: 'Loop colostomy' },
            { value: 'double-barrel', label: 'Double-barrel colostomy' },
        ],
    }),
    ostomyAge: z
        .string()
        .optional()
        .register(registry, {
        label: 'Ostomy Age / Status',
        options: [
            { value: 'established', label: 'Established - >6 weeks' },
            { value: 'new-6wk', label: 'New - <6 weeks' },
            { value: 'newly-formed', label: 'Newly formed - <2 weeks' },
        ],
    }),
    ostomySurgeryDate: z.string().optional().register(registry, { label: 'Date of Surgery' }),
    recentComplication: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Recent complications',
        options: [
            { value: 'no-complications', label: 'No recent complications' },
            { value: 'recent-hospitalization', label: 'Recent hospitalization' },
            { value: 'recent-ed-visit', label: 'Recent ED visit' },
            { value: 'active-complication', label: 'Active complication - MD notification required' },
        ],
    }),
    hospitalizationDetail: z.string().optional().register(registry, { label: 'Hospitalization Detail' }),
    stomaColor: z
        .string()
        .optional()
        .register(registry, {
        label: 'Stoma Color',
        options: [
            { value: 'pink-red', label: 'Pink-red - normal' },
            { value: 'pale-pink', label: 'Pale pink - possible anemia' },
            { value: 'bright-red', label: 'Bright red - irritated' },
            { value: 'dusky-purple', label: 'Dusky purple - ischemia' },
            { value: 'grey', label: 'Grey - compromised vascular supply' },
            { value: 'black-dark', label: 'Black / dark - necrosis' },
        ],
    }),
    stomaShape: z
        .string()
        .optional()
        .register(registry, {
        label: 'Stoma Shape',
        options: [
            { value: 'round', label: 'Round - expected' },
            { value: 'oval', label: 'Oval - acceptable' },
            { value: 'irregular', label: 'Irregular - assess cause' },
        ],
    }),
    stomaHeight: z
        .string()
        .optional()
        .register(registry, {
        label: 'Stoma Height / Profile',
        options: [
            { value: 'budded', label: 'Budded - 1–3 cm' },
            { value: 'flush', label: 'Flush - level with skin' },
            { value: 'retracted', label: 'Retracted - below skin' },
            { value: 'prolapsed', label: 'Prolapsed - >3 cm' },
        ],
    }),
    stomaWidthMm: z.string().optional().register(registry, { label: 'Width (mm)' }),
    stomaHeightMm: z.string().optional().register(registry, { label: 'Height (mm)' }),
    stomaSizeChange: z
        .string()
        .optional()
        .register(registry, {
        label: 'Size Change',
        options: [
            { value: 'consistent', label: 'Consistent with prior' },
            { value: 'edema', label: 'Edema - enlarged' },
            { value: 'shrinking', label: 'Shrinking - normal post-op' },
        ],
    }),
    stomaMucosa: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Mucosal Integrity',
        options: [
            { value: 'moist-intact', label: 'Moist - intact mucosa' },
            { value: 'dry', label: 'Dry - dehydration concern' },
            { value: 'mucosal-bleeding-minor', label: 'Mucosal bleeding - minor' },
            { value: 'active-bleeding', label: 'Active bleeding - apply pressure, MD notified' },
            { value: 'mucocutaneous-separation', label: 'Mucocutaneous separation' },
            { value: 'stenosis', label: 'Stenosis - tight / difficult to assess' },
        ],
    }),
    stomaFunction: z
        .string()
        .optional()
        .register(registry, {
        label: 'Stoma Function',
        options: [
            { value: 'active', label: 'Active - output present' },
            { value: 'no-output-home', label: 'No output - reports output at home' },
            { value: 'no-output-24h', label: 'No output >24 hrs - MD notified' },
            { value: 'blockage', label: 'Blockage suspected - MD notified' },
        ],
    }),
    skinZone1: z
        .string()
        .optional()
        .register(registry, {
        label: 'Zone 1: Under wafer (0–3 cm)',
        options: [
            { value: 'intact', label: 'Intact' },
            { value: 'erythema', label: 'Erythema - mild redness' },
            { value: 'excoriation', label: 'Excoriation - skin broken' },
            { value: 'maceration', label: 'Maceration - moisture damage' },
            { value: 'ulceration', label: 'Ulceration - open breakdown' },
            { value: 'fungal', label: 'Fungal rash' },
            { value: 'pseudoverrucous', label: 'Pseudoverrucous lesions - chronic moisture exposure' },
        ],
    }),
    skinZone2: z
        .string()
        .optional()
        .register(registry, {
        label: 'Zone 2: Wafer edge (3–6 cm)',
        options: [
            { value: 'intact', label: 'Intact' },
            { value: 'erythema', label: 'Erythema' },
            { value: 'tape-reaction', label: 'Tape / adhesive reaction' },
            { value: 'folliculitis', label: 'Folliculitis' },
            { value: 'contact-dermatitis', label: 'Contact dermatitis' },
        ],
    }),
    skinZone3: z
        .string()
        .optional()
        .register(registry, {
        label: 'Zone 3: Surrounding skin overall',
        options: [
            { value: 'intact', label: 'Intact - no issues' },
            { value: 'scarring', label: 'Scarring from prior surgeries' },
            { value: 'skin-folds', label: 'Skin folds - pouching challenge' },
            { value: 'parastomal-hernia', label: 'Parastomal hernia' },
            { value: 'fistula', label: 'Fistula' },
        ],
    }),
    leakageHistory: z
        .string()
        .optional()
        .register(registry, {
        label: 'Leakage Since Last Visit',
        options: [
            { value: 'no-leakage', label: 'No leakage - seal maintained' },
            { value: 'minor-seepage', label: 'Minor seepage - patient managed' },
            { value: 'leakage-episode', label: 'Leakage episode - skin exposure' },
            { value: 'frequent-leakage', label: 'Frequent leakage - reassess system' },
        ],
    }),
    leakageCause: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Leakage cause',
        options: [
            { value: 'wafer-too-large', label: 'Wafer opening too large' },
            { value: 'poor-seal-contour', label: 'Poor seal - skin contour / folds' },
            { value: 'adhesive-failure', label: 'Adhesive failure - heat / activity' },
            { value: 'retracted-stoma', label: 'Retracted stoma - seal impossible' },
            { value: 'high-output', label: 'High output overwhelming seal' },
            { value: 'parastomal-hernia-seal', label: 'Parastomal hernia disrupting seal' },
            { value: 'delayed-change', label: 'Patient delayed pouch change' },
        ],
    }),
    applianceSystem: z
        .string()
        .optional()
        .register(registry, {
        label: 'Appliance System',
        options: [
            { value: 'one-piece-drainable', label: 'One-piece drainable' },
            { value: 'one-piece-closed', label: 'One-piece closed-end' },
            { value: 'two-piece-drainable', label: 'Two-piece drainable' },
            { value: 'two-piece-closed', label: 'Two-piece closed-end' },
            { value: 'urostomy-open', label: 'Urostomy / open-end' },
            { value: 'high-output', label: 'High-output pouch' },
            { value: 'irrigation', label: 'Colostomy irrigation - no pouch' },
        ],
    }),
    waferType: z
        .string()
        .optional()
        .register(registry, {
        label: 'Wafer / Skin Barrier Type',
        options: [
            { value: 'flat', label: 'Flat' },
            { value: 'convex-mild', label: 'Convex - mild' },
            { value: 'convex-deep', label: 'Convex - deep' },
            { value: 'flexible', label: 'Flexible / soft' },
            { value: 'extended-wear', label: 'Extended wear' },
        ],
    }),
    applianceBrand: z
        .string()
        .optional()
        .register(registry, {
        label: 'Brand / Product',
        options: [
            { value: 'hollister', label: 'Hollister' },
            { value: 'coloplast', label: 'Coloplast' },
            { value: 'convatec', label: 'ConvaTec' },
            { value: 'salts', label: 'Salts Healthcare' },
            { value: 'nu-hope', label: 'Nu-Hope' },
            { value: 'other', label: 'Other' },
        ],
    }),
    accessories: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Accessories',
        options: [
            { value: 'barrier-ring', label: 'Barrier ring / moldable ring' },
            { value: 'paste', label: 'Paste - fill irregular skin' },
            { value: 'powder', label: 'Powder - skin protectant' },
            { value: 'stoma-belt', label: 'Stoma belt' },
            { value: 'convex-insert', label: 'Convex insert' },
            { value: 'barrier-strips', label: 'Barrier strips / tape frame' },
            { value: 'deodorant', label: 'Deodorant / odor control' },
            { value: 'skin-prep', label: 'Skin prep / wipes' },
        ],
    }),
    pouchChangeThisVisit: z
        .string()
        .optional()
        .register(registry, {
        label: 'Pouch Change This Visit',
        options: [
            { value: 'full-change', label: 'Yes - full pouch and wafer change' },
            { value: 'emptied-only', label: 'Yes - pouch emptied and assessed only' },
            { value: 'reinforced', label: 'Wafer reinforced - not due for change' },
            { value: 'no-change', label: 'No - within wear time, patient managing' },
        ],
    }),
    daysSinceWaferChange: z.string().optional().register(registry, { label: 'Days Since Last Wafer Change' }),
    waferOpeningMm: z.string().optional().register(registry, { label: 'Wafer Opening Size (mm)' }),
    outputConsistency: z
        .string()
        .optional()
        .register(registry, {
        label: 'Output Consistency',
        options: [
            { value: 'formed', label: 'Formed - expected sigmoid/descending' },
            { value: 'semi-formed', label: 'Semi-formed - normal transverse' },
            { value: 'mushy', label: 'Mushy / soft' },
            { value: 'loose', label: 'Loose / liquid - ileostomy concern' },
            { value: 'watery', label: 'Watery - high-output / assess hydration' },
        ],
    }),
    outputColor: z
        .string()
        .optional()
        .register(registry, {
        label: 'Output Color',
        options: [
            { value: 'brown', label: 'Brown - normal' },
            { value: 'tan-yellow', label: 'Tan / yellow - diet-related' },
            { value: 'dark-green', label: 'Dark green - bile / rapid transit' },
            { value: 'melena', label: 'Black / melena - MD notified' },
            { value: 'bright-red', label: 'Bright red blood - MD notified' },
            { value: 'yellow-urostomy', label: 'Yellow (urostomy) - normal' },
            { value: 'cloudy-urostomy', label: 'Cloudy / sediment (urostomy) - assess' },
        ],
    }),
    outputVolume: z
        .string()
        .optional()
        .register(registry, {
        label: 'Output Volume / Frequency',
        options: [
            { value: 'normal', label: 'Normal for patient baseline' },
            { value: 'decreased', label: 'Decreased - constipation concern' },
            { value: 'increased', label: 'Increased - dietary or illness' },
            { value: 'high-output', label: 'High output >1500 mL/day - MD notified' },
            { value: 'no-output-24h', label: 'No output >24 hrs - MD notified' },
        ],
    }),
    outputOdor: z
        .string()
        .optional()
        .register(registry, {
        label: 'Odor',
        options: [
            { value: 'expected', label: 'Expected - minimal' },
            { value: 'noticeable', label: 'Noticeable - deodorant added' },
            { value: 'foul', label: 'Foul - assess for infection' },
        ],
    }),
    whoManages: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Who Manages Ostomy Care',
        options: [
            { value: 'patient-independent', label: 'Patient - independent' },
            { value: 'caregiver-primary', label: 'Caregiver - primary manager' },
            { value: 'shared', label: 'Shared - patient and caregiver' },
            { value: 'nurse-only', label: 'Nurse only - patient unable' },
        ],
    }),
    skillLevel: z
        .string()
        .optional()
        .register(registry, {
        label: 'Self-Management Skill Level',
        options: [
            { value: 'independent', label: 'Independent - correct technique' },
            { value: 'mostly-independent', label: 'Mostly independent - minor gaps' },
            { value: 'verbal-cueing', label: 'Requires verbal cueing' },
            { value: 'hands-on', label: 'Requires hands-on assistance' },
            { value: 'unable', label: 'Unable - POC modification needed' },
        ],
    }),
    techniqueObserved: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Technique observed',
        options: [
            { value: 'pouch-emptied-correct', label: 'Pouch emptied correctly' },
            { value: 'wafer-removed-atraumatic', label: 'Wafer removed without skin trauma' },
            { value: 'stoma-measured', label: 'Stoma measured before cutting wafer' },
            { value: 'skin-cleaned-dried', label: 'Skin cleaned and dried prior to wafer' },
            { value: 'wafer-sealed', label: 'Wafer sealed - no gaps' },
            { value: 'wafer-too-large', label: 'Wafer opening too large - stool contacting skin' },
            { value: 'improper-cleaning', label: 'Improper skin cleaning - corrected' },
            { value: 'seal-not-pressed', label: 'Seal not fully pressed - reinstructed' },
            { value: 'significant-errors', label: 'Significant technique errors - demonstration required' },
        ],
    }),
    educationTopics: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Topics Addressed This Visit',
        options: [
            { value: 'pouch-technique', label: 'Pouch change technique - demonstrated' },
            { value: 'stoma-warning-signs', label: 'Stoma color and warning signs' },
            { value: 'peristomal-skin-care', label: 'Peristomal skin care' },
            { value: 'diet-output', label: 'Diet and output management' },
            { value: 'fluid-intake', label: 'Fluid intake - high-output prevention' },
            { value: 'activity-bathing', label: 'Activity and bathing with ostomy' },
            { value: 'when-to-call', label: 'When to call nurse / MD / 911' },
            { value: 'odor-management', label: 'Odor management' },
            { value: 'supply-ordering', label: 'Supply ordering and reorder process' },
            { value: 'cwocn-referral', label: 'CWOCN / enterostomal nurse referral discussed' },
            { value: 'return-to-activity', label: 'Return to activity / body image / support group' },
        ],
    }),
    educationLearner: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Learner',
        options: [
            { value: 'patient', label: 'Patient' },
            { value: 'spouse', label: 'Spouse' },
            { value: 'caregiver', label: 'Caregiver' },
            { value: 'adult-child', label: 'Adult child' },
        ],
    }),
    educationComprehension: z
        .string()
        .optional()
        .register(registry, {
        label: 'Comprehension / Response',
        options: [
            { value: 'teach-back', label: 'Return teach-back - demonstrated correctly' },
            { value: 'partial', label: 'Partial understanding - reinforce next visit' },
            { value: 'unable', label: 'Unable to demonstrate - modify approach / CWOCN referral' },
        ],
    }),
    supplyAdequacy: z
        .string()
        .optional()
        .register(registry, {
        label: 'Supply Adequacy',
        options: [
            { value: 'adequate', label: 'Adequate supplies on hand' },
            { value: 'low', label: 'Low supply - reorder initiated' },
            { value: 'no-supplies', label: 'No supplies - emergency order placed' },
            { value: 'insurance-issue', label: 'Insurance / coverage issue - social work notified' },
        ],
    }),
    abnormalStomaAppearance: z.boolean().optional().register(registry, { label: 'Abnormal stoma appearance' }),
    abnormalSurroundingSkin: z.boolean().optional().register(registry, { label: 'Abnormal surrounding skin' }),
    requiresTraining: z.boolean().optional().register(registry, { label: 'Patient/caregiver require training' }),
    dialysis: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Dialysis',
        options: [
            { value: 'hemodialysis', label: 'Hemodialysis' },
            { value: 'peritoneal', label: 'Peritoneal' },
            { value: 'av-fistula', label: 'AV fistula (shunt)' },
            { value: 'thrill-bruit-non-palpable', label: 'Thrill/bruit, non-palpable' },
            { value: 'catheter-infection-ss', label: 'Catheter s/s of infection' },
            { value: 'physician-notified-catheter', label: 'Physician notified: abnormality of catheter' },
            { value: 'cm-notified-catheter', label: 'Clinical Manager notified: abnormality of catheter' },
        ],
    }),
    mdNotifTime: z.string().optional().register(registry, { label: 'MD Notification Time' }),
    mdOrders: z.string().optional().register(registry, { label: 'MD Orders Received' }),
    ostomyNarrative: z.string().optional().register(registry, { label: 'Clinical Narrative' }),
    ostomyChange: z
        .string()
        .optional()
        .register(registry, {
        label: 'Change From Last Visit',
        options: [
            { value: 'no-change', label: 'No change' },
            { value: 'change-noted', label: 'Change noted - see narrative' },
        ],
    }),
})
    .register(registry, { label: ostomyCardTitles.root });
export const baseOstomySchemaRefined = baseOstomySchema.refine((data) => sectionHasValue(data), {
    message: 'Mark as within normal limits if none applies',
    path: [],
});
